import React, { useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import { Box, Flex, Select, SimpleGrid, styled } from '@driftt/dds'

import { teams } from 'modules/teams/selectors'
import { trackEvent } from 'utils/tracking'

import { KPICardConstants } from './constants'
import {
  getIncompleteSalesReps,
  getMetrics,
  getPercentage,
  getStatusIndicator,
} from './helpers'
import SalesRepKPICard from './SalesRepKPICard'
import SalesRepTable from './SalesRepTable'
import { useSalesSetupData } from './useSalesRepSetup.hook'

const FilterDivider = styled(Box, {
  width: '1px',
  height: '$4',
  background: '$gray3',
})

const SalesRepSetupContainerV2 = () => {
  const allTeams = useSelector(teams)
  const [selectedFilters, setSelectedFilters] = useState<number[]>([])

  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([])
  const [selectedRoleNames, setSelectedRoleNames] = useState<string[]>([])

  const { isLoading, salesSetup, roles } = useSalesSetupData({
    selectedTeamIds,
    selectedRoleNames,
  })

  const metrics = getMetrics(salesSetup)

  const formatMetric = (metric) => {
    return `${getPercentage(metric, salesSetup)}%`
  }

  const availableOptions = useMemo(
    () =>
      allTeams.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    [allTeams]
  )

  const avialableRoles = useMemo(
    () =>
      roles.map((role) => ({
        label: role,
        value: role,
      })),
    [roles]
  )

  return (
    <Flex direction="column" gap={3}>
      <Flex gap={3}>
        <Select
          label="Team"
          hideLabel
          placeholder="Select team..."
          size="medium"
          multi
          options={availableOptions}
          onChange={(team: any) => {
            setSelectedFilters(team.map((item) => item.value))
            trackEvent('SALES_REP_SETUP_TEAM_CHANGED', {
              teams: team.length,
            })
          }}
          onBlur={() => setSelectedTeamIds(selectedFilters)}
        />
        <FilterDivider />
        <Select
          label="Role"
          hideLabel
          placeholder="Select roles..."
          size="medium"
          multi
          options={avialableRoles || []}
          onChange={(role: any) =>
            setSelectedRoleNames(role.map((item) => item.value))
          }
        />
      </Flex>
      <SimpleGrid columns={3} gap={3}>
        {KPICardConstants.map((card) => {
          return (
            <SalesRepKPICard
              key={card.id}
              metric={metrics[card.metricIdentifier]}
              incompleteSalesRepMetric={getIncompleteSalesReps(
                card.metricIdentifier,
                salesSetup
              )}
              status={getStatusIndicator(
                metrics[card.metricIdentifier],
                salesSetup
              )}
              formatMetric={formatMetric}
              title={card.title}
              description={card.description}
              metricIdentifier={card.metricIdentifier}
              isLoading={isLoading}
            />
          )
        })}
      </SimpleGrid>
      <SalesRepTable
        tableData={salesSetup}
        isLoading={isLoading}
        selectedTeamIds={selectedTeamIds}
      />
    </Flex>
  )
}

export default SalesRepSetupContainerV2
